import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/joy";
import SearchIcon from "@mui/icons-material/Search";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import Page from "../components/Pages/Page";
import PageHeader from "../components/PageHeader";
import Loading from "../components/Loading";
import PageError from "../components/PageError";
import JsonSchemaForm from "../components/JsonSchemaForm";
import DisableImpactModal from "../components/AdminIntegrations/DisableImpactModal";
import TierACard from "../components/AdminIntegrations/TierACard";
import TierBCard from "../components/AdminIntegrations/TierBCard";
import TierCCard from "../components/AdminIntegrations/TierCCard";
import OAuthConnectButton from "../components/Integrations/OAuthConnectButton";
import SharePointSitesManager from "../components/AdminIntegrations/SharePointSitesManager";
import EmailSendersManager from "../components/AdminIntegrations/EmailSendersManager";
import {
  credentialsSchemaForEntry,
  firstSecretValue,
  hasSchemaFields,
  isGenericOAuthAuthorizationCodeEntry,
  omitUserCredentialSchemaProperties,
  schemaForEntry,
  secretFieldsForCatalogEntry,
  withoutSecretFields,
} from "../components/AdminIntegrations/integrationConfigUtils";
import {
  OrganizationIntegrationDisableImpact,
  OrganizationIntegration,
  OrganizationIntegrationCreate,
  OrganizationIntegrationUpdate,
  DeltekConnectionTestRead,
  User,
} from "../common/types";
import {
  catalogKeyForLegacyType,
  useIntegrationCatalog,
} from "../services/integrationCatalog";
import {
  useCreateOrganizationIntegration,
  useDeleteOrganizationIntegration,
  useListOrganizationIntegrations,
  useReadOrganizationIntegrationDisableImpact,
  useReadUser,
  useTestDeltekConnection,
  useToggleIntegrationPermissionVerification,
  useUpdateOrganizationIntegration,
} from "../services/api";
import {
  IntegrationCatalogEntry,
  JsonSchemaObject,
} from "../types/integrationCatalog";

type ConfigActionMode =
  "enable" | "edit" | "edit-credentials" | "manage-policy";

interface ConfigActionState {
  mode: ConfigActionMode;
  entry: IntegrationCatalogEntry;
  integration?: OrganizationIntegration;
}

interface OAuthCallbackFeedback {
  color: "success" | "danger";
  message: string;
}

interface DeltekConnectionFeedback {
  color: "success" | "warning" | "danger";
  message: string;
}

interface AdminIntegrationsViewProps {
  catalogEntries: IntegrationCatalogEntry[];
  integrations: OrganizationIntegration[];
  orgId: string;
  loading?: boolean;
  error?: string | null;
  usingStub?: boolean;
  showDeprecated?: boolean;
  onToggleShowDeprecated?: (nextValue: boolean) => void;
  onSaveConfig?: (
    entry: IntegrationCatalogEntry,
    values: Record<string, any>,
    targetIntegration?: OrganizationIntegration,
  ) => Promise<void>;
  onDeleteConfig?: (integration: OrganizationIntegration) => Promise<void>;
  onDisable?: (integration: OrganizationIntegration) => Promise<void>;
  onReadDisableImpact?: (
    integrationKey: string,
  ) => Promise<OrganizationIntegrationDisableImpact>;
  onTestDeltekConnection?: (
    integration: OrganizationIntegration,
  ) => Promise<DeltekConnectionTestRead>;
  oauthFeedback?: OAuthCallbackFeedback | null;
}

const OAUTH_CALLBACK_QUERY_PARAMS = [
  "oauth_status",
  "oauth_error",
  "integration_key",
  "connection_id",
];

export const oauthCallbackFeedbackFromSearch = (
  search: string,
): OAuthCallbackFeedback | null => {
  const params = new URLSearchParams(search);
  const status = params.get("oauth_status");
  if (status === "success") {
    return {
      color: "success",
      message: "Integration connected successfully.",
    };
  }
  if (status !== "error") return null;

  const error = params.get("oauth_error") || "unknown_error";
  const normalizedError = error.toLowerCase();
  if (normalizedError.includes("key and secret are invalid")) {
    return {
      color: "danger",
      message:
        "Deltek rejected the credentials. Verify the Consumer Key and Secret in your Deltek VP OAuth app admin.",
    };
  }
  if (
    normalizedError.includes("oauth state cookie is missing") ||
    normalizedError.includes("oauth state is invalid")
  ) {
    return {
      color: "danger",
      message: "Browser session lost. Refresh the page and retry.",
    };
  }

  return {
    color: "danger",
    message: `OAuth connection failed: ${error}`,
  };
};

export const removeOAuthCallbackParams = (search: string): string => {
  const params = new URLSearchParams(search);
  OAUTH_CALLBACK_QUERY_PARAMS.forEach((paramName) => params.delete(paramName));
  const nextSearch = params.toString();
  return nextSearch ? `?${nextSearch}` : "";
};

export const deltekConnectionFeedbackFromResult = (
  result: DeltekConnectionTestRead,
): DeltekConnectionFeedback => {
  if (result.connected && result.state === "ready") {
    return {
      color: "success",
      message: "Deltek connection succeeded.",
    };
  }
  if (result.connected && result.state === "partial") {
    return {
      color: "warning",
      message:
        "Deltek connection succeeded, but one or more metadata resources are unavailable.",
    };
  }
  return {
    color: "danger",
    message:
      result.action ||
      "Deltek connection test failed. Verify the saved service-account configuration and try again.",
  };
};

const integrationsForEntry = (
  entry: IntegrationCatalogEntry,
  integrations: OrganizationIntegration[],
) =>
  integrations.filter(
    (integration) =>
      integration.id === entry.org_integration_id ||
      catalogKeyForLegacyType(integration.type) === entry.key,
  );

const integrationForEntry = (
  entry: IntegrationCatalogEntry,
  integrations: OrganizationIntegration[],
) => {
  const matches = integrationsForEntry(entry, integrations);
  if (matches.length === 0) return undefined;
  // Prefer an exact `org_integration_id` match when the catalog entry already
  // points at a specific row; otherwise return the first matching row.
  return (
    matches.find(
      (integration) => integration.id === entry.org_integration_id,
    ) || matches[0]
  );
};

// An entry is "enabled" when it has an active integration row or the catalog
// already reports it as org-enabled. Shared by card rendering and filtering so
// the two never diverge.
const isEntryEnabled = (
  entry: IntegrationCatalogEntry,
  integrations: OrganizationIntegration[],
) =>
  integrationsForEntry(entry, integrations).some(
    (i) => i.status === "active",
  ) || entry.org_enabled === true;

const policySchemaForEntry = (entry: IntegrationCatalogEntry) =>
  schemaForEntry(entry);

const HUBSPOT_SECRET_FIELDS = [
  "token",
  "client_secret",
  "token_secret_reference",
  "client_secret_reference",
];

const secretFieldsForEntry = (entry?: IntegrationCatalogEntry | null) => {
  if (entry?.key === "hubspot") {
    return ["token", "client_secret"];
  }
  return secretFieldsForCatalogEntry(entry);
};

const cloneSchema = (
  schema?: JsonSchemaObject | null,
): JsonSchemaObject | null => {
  if (!schema) return null;
  return JSON.parse(JSON.stringify(schema));
};

const hubSpotUiSchema = (
  entry: IntegrationCatalogEntry,
): JsonSchemaObject | null => {
  const schema = cloneSchema(schemaForEntry(entry)) || {
    type: "object",
    properties: {},
  };
  const properties = { ...(schema.properties || {}) };

  delete properties.token_secret_reference;
  delete properties.client_secret_reference;
  properties.token = {
    type: "string",
    title: "Private app access token",
    format: "password",
  };
  properties.client_secret = {
    type: "string",
    title: "OAuth client secret",
    format: "password",
  };

  return {
    ...schema,
    properties,
    required: ["token"],
  };
};

export const configAndSecretForEntry = (
  entry: IntegrationCatalogEntry,
  values: Record<string, any>,
) => {
  if (entry.key !== "hubspot") {
    const secretFields = secretFieldsForEntry(entry);
    return {
      config: withoutSecretFields(values, secretFields),
      secretKey: firstSecretValue(values, secretFields),
    };
  }

  const config = { ...values };
  HUBSPOT_SECRET_FIELDS.forEach((field) => {
    delete config[field];
  });

  const token = values.token || values.token_secret_reference;
  const clientSecret = values.client_secret || values.client_secret_reference;
  const secretPayload: Record<string, string> = {};
  if (typeof token === "string" && token.trim()) {
    secretPayload.token = token.trim();
  }
  if (typeof clientSecret === "string" && clientSecret.trim()) {
    secretPayload.client_secret = clientSecret.trim();
  }

  return {
    config,
    secretKey:
      Object.keys(secretPayload).length > 0
        ? JSON.stringify(secretPayload)
        : undefined,
  };
};

const isSubstrateGrowable = (entry: IntegrationCatalogEntry) =>
  entry.substrate_growable === true ||
  entry.key === "sharepoint" ||
  entry.key === "email";

const substratePreview = (
  entry: IntegrationCatalogEntry,
  integration?: OrganizationIntegration,
) => {
  if (!integration) return null;

  if (entry.key === "sharepoint") {
    const count = Array.isArray(integration.config?.site_urls)
      ? integration.config.site_urls.length
      : (entry.substrate_item_count ?? 0);
    return `${count} SharePoint site${count === 1 ? "" : "s"} configured`;
  }

  if (entry.key === "email") {
    const count =
      entry.substrate_item_count ??
      (Array.isArray(integration.config?.sender_addresses)
        ? integration.config.sender_addresses.length
        : integration.config?.default_sender_address
          ? 1
          : 0);
    return `${count} sender${count === 1 ? "" : "s"} configured`;
  }

  if (entry.key === "url_crawler") {
    return "Organization policy defaults are configured here; workspaces pick their crawl URLs separately.";
  }

  return null;
};

const modalTitle = (action: ConfigActionState | null) => {
  if (!action) return "";
  switch (action.mode) {
    case "enable":
      return `Enable ${action.entry.display_name}`;
    case "edit":
      return `Edit ${action.entry.display_name}`;
    case "edit-credentials":
      return `Edit ${action.entry.display_name} credentials`;
    case "manage-policy":
      return `Manage ${action.entry.display_name} substrate`;
    default:
      return action.entry.display_name;
  }
};

const submitLabel = (action: ConfigActionState | null) => {
  switch (action?.mode) {
    case "enable":
      return "Enable";
    case "manage-policy":
      return "Save substrate";
    default:
      return "Save";
  }
};

const modalSchemaForAction = (action: ConfigActionState | null) => {
  if (!action) return null;
  let schema: JsonSchemaObject | null = null;

  if (action.entry.key === "hubspot") {
    schema = hubSpotUiSchema(action.entry);
  } else {
    switch (action.mode) {
      case "enable":
      case "edit-credentials":
        schema =
          action.entry.archetype === "org_workspace"
            ? credentialsSchemaForEntry(action.entry)
            : schemaForEntry(action.entry);
        break;
      case "edit":
        schema = schemaForEntry(action.entry);
        break;
      case "manage-policy":
        schema = policySchemaForEntry(action.entry);
        break;
      default:
        schema = null;
    }
  }

  if (isGenericOAuthAuthorizationCodeEntry(action.entry)) {
    return omitUserCredentialSchemaProperties(schema);
  }

  return schema;
};

const oauthOrganizationIntegrationIdForAction = (
  action: ConfigActionState | null,
) =>
  action?.integration?.id ||
  action?.entry.org_integration_id ||
  action?.entry.organization_integration_id ||
  null;

export const AdminIntegrationsView: React.FC<AdminIntegrationsViewProps> = ({
  catalogEntries,
  integrations,
  orgId,
  loading = false,
  error = null,
  usingStub = false,
  showDeprecated = false,
  onToggleShowDeprecated,
  onSaveConfig,
  onDeleteConfig,
  onDisable,
  onReadDisableImpact,
  onTestDeltekConnection,
  oauthFeedback = null,
}) => {
  const [configAction, setConfigAction] = useState<ConfigActionState | null>(
    null,
  );
  const [disableAction, setDisableAction] = useState<{
    entry: IntegrationCatalogEntry;
    integration: OrganizationIntegration;
  } | null>(null);
  const [disableImpact, setDisableImpact] =
    useState<OrganizationIntegrationDisableImpact | null>(null);
  const [disableImpactLoading, setDisableImpactLoading] = useState(false);
  const [deleteIntegration, setDeleteIntegration] =
    useState<OrganizationIntegration | null>(null);
  const [sharePointManagerIntegrationId, setSharePointManagerIntegrationId] =
    useState<string | null>(null);
  const [emailManagerIntegrationId, setEmailManagerIntegrationId] = useState<
    string | null
  >(null);
  const [formValue, setFormValue] = useState<Record<string, any>>({});
  const [permVerEnabled, setPermVerEnabled] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [connectionTestingId, setConnectionTestingId] = useState<string | null>(
    null,
  );
  const [deltekConnectionFeedback, setDeltekConnectionFeedback] =
    useState<DeltekConnectionFeedback | null>(null);

  // Client-side catalog filters (no backend changes).
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const categoryOptions = useMemo(() => {
    const set = new Set<string>();
    catalogEntries.forEach((entry) => {
      if (entry.category) set.add(entry.category);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [catalogEntries]);

  const visibleEntries = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return catalogEntries.filter((entry) => {
      if (needle) {
        const haystack =
          `${entry.display_name || ""} ${entry.key}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      if (categoryFilter !== "all" && entry.category !== categoryFilter) {
        return false;
      }
      if (statusFilter !== "all") {
        const enabled = isEntryEnabled(entry, integrations);
        if (statusFilter === "enabled" && !enabled) return false;
        if (statusFilter === "not_enabled" && enabled) return false;
      }
      return true;
    });
  }, [catalogEntries, integrations, search, categoryFilter, statusFilter]);

  const togglePermissionVerification =
    useToggleIntegrationPermissionVerification();

  const openConfigAction = (
    action: ConfigActionMode,
    entry: IntegrationCatalogEntry,
    integration?: OrganizationIntegration,
  ) => {
    setConfigAction({ mode: action, entry, integration });
    setFormValue(integration?.config || {});
    setPermVerEnabled(integration?.permission_verification_enabled ?? false);
    setModalError(null);
  };

  const closeConfigAction = () => {
    setConfigAction(null);
    setFormValue({});
    setPermVerEnabled(false);
    setModalError(null);
  };

  const handleSaveAction = async () => {
    if (!configAction || !onSaveConfig) return;

    setSaving(true);
    setModalError(null);
    try {
      await onSaveConfig(
        configAction.entry,
        formValue,
        configAction.integration,
      );

      // For SharePoint edit-credentials, also sync the permission verification toggle
      if (
        configAction.mode === "edit-credentials" &&
        configAction.entry.key === "sharepoint" &&
        configAction.integration
      ) {
        const currentEnabled =
          configAction.integration.permission_verification_enabled ?? false;
        if (permVerEnabled !== currentEnabled) {
          await togglePermissionVerification(
            orgId,
            configAction.integration.id,
            permVerEnabled,
          );
        }
      }

      closeConfigAction();
    } catch (saveError: any) {
      setModalError(
        saveError?.message || "Failed to save integration configuration.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDirectEnable = async (entry: IntegrationCatalogEntry) => {
    if (!onSaveConfig) return;
    setSaving(true);
    setModalError(null);
    try {
      await onSaveConfig(entry, {});
    } catch (saveError: any) {
      setModalError(saveError?.message || "Failed to enable integration.");
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async () => {
    if (!disableAction || !onDisable) return;

    setSaving(true);
    setModalError(null);
    try {
      await onDisable(disableAction.integration);
      setDisableAction(null);
      setDisableImpact(null);
    } catch (disableError: any) {
      setModalError(disableError?.message || "Failed to disable integration.");
    } finally {
      setSaving(false);
    }
  };

  const openDisableImpactModal = async (
    entry: IntegrationCatalogEntry,
    integration: OrganizationIntegration,
  ) => {
    setDisableAction({ entry, integration });
    setDisableImpact(null);
    setDisableImpactLoading(Boolean(onReadDisableImpact));
    setModalError(null);

    if (!onReadDisableImpact) {
      setDisableImpactLoading(false);
      return;
    }

    try {
      const impact = await onReadDisableImpact(entry.key);
      setDisableImpact(impact);
    } catch (impactError: any) {
      setModalError(
        impactError?.message || "Failed to load integration blast radius.",
      );
    } finally {
      setDisableImpactLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteIntegration || !onDeleteConfig) return;

    setSaving(true);
    setModalError(null);
    try {
      await onDeleteConfig(deleteIntegration);
      setDeleteIntegration(null);
    } catch (deleteError: any) {
      setModalError(
        deleteError?.message || "Failed to delete integration configuration.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleTestDeltekConnection = async (
    integration: OrganizationIntegration,
  ) => {
    if (!onTestDeltekConnection) return;
    setConnectionTestingId(integration.id);
    setDeltekConnectionFeedback(null);
    try {
      const result = await onTestDeltekConnection(integration);
      setDeltekConnectionFeedback(deltekConnectionFeedbackFromResult(result));
    } catch {
      setDeltekConnectionFeedback({
        color: "danger",
        message:
          "Deltek connection test is temporarily unavailable. Try again without changing the saved configuration.",
      });
    } finally {
      setConnectionTestingId(null);
    }
  };

  const renderCard = (entry: IntegrationCatalogEntry) => {
    const matchingIntegrations = integrationsForEntry(entry, integrations);
    const integration = integrationForEntry(entry, integrations);
    const enabled = isEntryEnabled(entry, integrations);

    if (entry.archetype === "no_config") {
      return (
        <TierCCard
          key={entry.key}
          entry={entry}
          integration={integration}
          enabled={enabled}
          saving={saving}
          onToggle={(nextEnabled) => {
            if (nextEnabled) {
              handleDirectEnable(entry);
            } else if (integration) {
              void openDisableImpactModal(entry, integration);
            }
          }}
        />
      );
    }

    if (entry.archetype === "org_only") {
      return (
        <TierBCard
          key={entry.key}
          entry={entry}
          integration={integration}
          enabled={enabled}
          saving={saving}
          onEnable={() => openConfigAction("enable", entry, integration)}
          onEdit={() => openConfigAction("edit", entry, integration)}
          onDisable={() =>
            integration && void openDisableImpactModal(entry, integration)
          }
          onDelete={() => integration && setDeleteIntegration(integration)}
          onTestConnection={
            entry.key === "deltek" &&
            entry.auth_model === "oauth_password" &&
            integration &&
            onTestDeltekConnection
              ? () => void handleTestDeltekConnection(integration)
              : undefined
          }
          connectionTesting={connectionTestingId === integration?.id}
        />
      );
    }

    const credentialSchema = credentialsSchemaForEntry(entry);
    const canEditCredentials = hasSchemaFields(credentialSchema);

    const handleManageSubstrate = (target: OrganizationIntegration) => {
      if (!isSubstrateGrowable(entry)) {
        openConfigAction("manage-policy", entry, target);
        return;
      }
      if (entry.key === "sharepoint") {
        setSharePointManagerIntegrationId(target.id);
        return;
      }
      if (entry.key === "email") {
        setEmailManagerIntegrationId(target.id);
        return;
      }
      openConfigAction("manage-policy", entry, target);
    };

    return (
      <TierACard
        key={entry.key}
        entry={entry}
        integrations={matchingIntegrations}
        enabled={enabled}
        saving={saving}
        preview={substratePreview(entry, integration)}
        rowPreview={(target) => substratePreview(entry, target)}
        canEditCredentials={canEditCredentials}
        onEnable={() => {
          if (
            hasSchemaFields(credentialSchema) ||
            isGenericOAuthAuthorizationCodeEntry(entry)
          ) {
            openConfigAction("enable", entry, integration);
          } else {
            handleDirectEnable(entry);
          }
        }}
        onManageSubstrate={handleManageSubstrate}
        onEditCredentials={(target) =>
          openConfigAction("edit-credentials", entry, target)
        }
        onDisable={(target) => void openDisableImpactModal(entry, target)}
      />
    );
  };

  const activeSchema = modalSchemaForAction(configAction);
  const oauthOrganizationIntegrationId =
    oauthOrganizationIntegrationIdForAction(configAction);
  const showOAuthConnectButton =
    isGenericOAuthAuthorizationCodeEntry(configAction?.entry) &&
    configAction?.mode !== "manage-policy" &&
    Boolean(oauthOrganizationIntegrationId);
  const showSchemaForm =
    !showOAuthConnectButton || hasSchemaFields(activeSchema);

  return (
    <Page>
      <PageHeader>
        <Box>
          <Typography level="h1">Integrations</Typography>
          <Typography level="body-sm" color="neutral">
            Manage no-config, org-level, and org + workspace integrations from a
            single catalog.
          </Typography>
        </Box>
      </PageHeader>

      <Stack spacing={2}>
        {usingStub && (
          <Alert color="warning" variant="soft">
            The backend catalog endpoint is not available yet. This page is
            rendering typed catalog metadata locally and will use the live
            endpoint when it lands.
          </Alert>
        )}
        {oauthFeedback && (
          <Alert color={oauthFeedback.color} variant="soft">
            {oauthFeedback.message}
          </Alert>
        )}
        {deltekConnectionFeedback && (
          <Alert color={deltekConnectionFeedback.color} variant="soft">
            {deltekConnectionFeedback.message}
          </Alert>
        )}
        {error && !usingStub && (
          <Alert color="danger" variant="soft">
            {error}
          </Alert>
        )}

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "flex-end" }}
              >
                <FormControl sx={{ flex: 2, minWidth: 200 }}>
                  <FormLabel>Search</FormLabel>
                  <Input
                    size="sm"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search integrations"
                    startDecorator={<SearchIcon />}
                  />
                </FormControl>
                <FormControl sx={{ flex: 1, minWidth: 160 }}>
                  <FormLabel>Category</FormLabel>
                  <Select
                    size="sm"
                    value={categoryFilter}
                    onChange={(_event, value) =>
                      setCategoryFilter(value ?? "all")
                    }
                  >
                    <Option value="all">All categories</Option>
                    {categoryOptions.map((category) => (
                      <Option key={category} value={category}>
                        {category}
                      </Option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ flex: 1, minWidth: 150 }}>
                  <FormLabel>Status</FormLabel>
                  <Select
                    size="sm"
                    value={statusFilter}
                    onChange={(_event, value) =>
                      setStatusFilter(value ?? "all")
                    }
                  >
                    <Option value="all">All statuses</Option>
                    <Option value="enabled">Enabled</Option>
                    <Option value="not_enabled">Not enabled</Option>
                  </Select>
                </FormControl>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ height: 32, flexShrink: 0 }}
                >
                  <Switch
                    checked={showDeprecated}
                    onChange={(event) =>
                      onToggleShowDeprecated?.(event.target.checked)
                    }
                  />
                  <Typography level="body-sm">Show deprecated</Typography>
                </Stack>
              </Stack>

              {loading ? (
                <Loading />
              ) : catalogEntries.length === 0 ? (
                <Alert color="neutral" variant="soft">
                  No integrations are available for this organization.
                </Alert>
              ) : visibleEntries.length === 0 ? (
                <Alert color="neutral" variant="soft">
                  No integrations match the current filters.
                </Alert>
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      lg: "repeat(2, minmax(0, 1fr))",
                    },
                    gap: 2,
                  }}
                >
                  {visibleEntries.map((entry) => renderCard(entry))}
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Modal open={Boolean(configAction)} onClose={closeConfigAction}>
        <ModalDialog sx={{ width: "min(760px, calc(100vw - 32px))" }}>
          <DialogTitle>{modalTitle(configAction)}</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              {modalError && (
                <Alert color="danger" variant="soft">
                  {modalError}
                </Alert>
              )}
              {showSchemaForm && (
                <JsonSchemaForm
                  schema={activeSchema}
                  secretFields={secretFieldsForEntry(configAction?.entry)}
                  storedSecretFields={secretFieldsForEntry(
                    configAction?.entry,
                  ).filter(
                    (field) =>
                      configAction?.integration?.secret_fields_set?.[field] ===
                      true,
                  )}
                  value={formValue}
                  onChange={setFormValue}
                  disabled={saving}
                  idPrefix={`admin-integration-${configAction?.entry.key || "config"}`}
                />
              )}
              {showOAuthConnectButton && configAction && (
                <>
                  <OAuthConnectButton
                    integrationKey={configAction.entry.key}
                    organizationIntegrationId={oauthOrganizationIntegrationId}
                    connection={
                      configAction.integration?.oauth_connection || null
                    }
                  />
                  {/* TODO Phase 3 polish: post-callback refresh */}
                </>
              )}
              {configAction?.mode === "edit-credentials" &&
                configAction?.entry.key === "sharepoint" && (
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{
                      mt: 1,
                      p: 1.5,
                      borderRadius: "sm",
                      bgcolor: "background.level1",
                    }}
                  >
                    <Switch
                      checked={permVerEnabled}
                      onChange={(e) => setPermVerEnabled(e.target.checked)}
                      disabled={saving}
                    />
                    <Stack spacing={0.25}>
                      <Typography level="title-sm">
                        Permission Verification
                      </Typography>
                      <Typography level="body-xs" color="neutral">
                        Filter documents based on SharePoint ACL permissions.
                        Users only see files they have access to in SharePoint.
                      </Typography>
                    </Stack>
                  </Stack>
                )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleSaveAction} loading={saving}>
              {submitLabel(configAction)}
            </Button>
            <Button variant="plain" color="neutral" onClick={closeConfigAction}>
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <DisableImpactModal
        open={Boolean(disableAction)}
        integrationName={disableAction?.integration.name}
        impact={disableImpact}
        loading={disableImpactLoading}
        error={modalError}
        saving={saving}
        onConfirm={handleDisable}
        onClose={() => {
          setDisableAction(null);
          setDisableImpact(null);
          setModalError(null);
        }}
      />

      <Modal
        open={Boolean(deleteIntegration)}
        onClose={() => setDeleteIntegration(null)}
      >
        <ModalDialog sx={{ width: "min(560px, calc(100vw - 32px))" }}>
          <DialogTitle>Delete configuration</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              {modalError && (
                <Alert color="danger" variant="soft">
                  {modalError}
                </Alert>
              )}
              <Typography level="body-sm">
                Delete the stored configuration for {deleteIntegration?.name}?
                This is separate from disable and clears the saved org-level
                credentials and config.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button color="danger" onClick={handleDelete} loading={saving}>
              Delete configuration
            </Button>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setDeleteIntegration(null)}
            >
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {sharePointManagerIntegrationId && (
        <SharePointSitesManager
          open={Boolean(sharePointManagerIntegrationId)}
          organizationId={orgId}
          organizationIntegrationId={sharePointManagerIntegrationId}
          onClose={() => setSharePointManagerIntegrationId(null)}
        />
      )}

      {emailManagerIntegrationId && (
        <EmailSendersManager
          open={Boolean(emailManagerIntegrationId)}
          organizationId={orgId}
          organizationIntegrationId={emailManagerIntegrationId}
          onClose={() => setEmailManagerIntegrationId(null)}
        />
      )}
    </Page>
  );
};

const AdminIntegrations: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const readUser = useReadUser();
  const listOrgIntegrations = useListOrganizationIntegrations();
  const createOrgIntegration = useCreateOrganizationIntegration();
  const updateOrgIntegration = useUpdateOrganizationIntegration();
  const deleteOrgIntegration = useDeleteOrganizationIntegration();
  const readDisableImpact = useReadOrganizationIntegrationDisableImpact();
  const testDeltekConnection = useTestDeltekConnection();
  const [user, setUser] = useState<User | null>(null);
  const [userLoading, setUserLoading] = useState(true);
  const [userError, setUserError] = useState<string | null>(null);
  const [integrations, setIntegrations] = useState<OrganizationIntegration[]>(
    [],
  );
  const [showDeprecated, setShowDeprecated] = useState(false);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [integrationsError, setIntegrationsError] = useState<string | null>(
    null,
  );
  const [oauthFeedback, setOAuthFeedback] =
    useState<OAuthCallbackFeedback | null>(null);

  useEffect(() => {
    const loadUser = async () => {
      try {
        setUserLoading(true);
        setUser(await readUser());
      } catch (err: any) {
        setUserError(err?.message || "Failed to load user.");
      } finally {
        setUserLoading(false);
      }
    };

    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const orgId = user?.organization_id || null;
  const isAdmin = user?.role_in_org === "admin";
  const {
    entries,
    loading: catalogLoading,
    error: catalogError,
    usingStub,
    refresh: refreshCatalog,
  } = useIntegrationCatalog(orgId, undefined, showDeprecated);

  const refreshIntegrations = useMemo(
    () => async () => {
      if (!orgId) return;
      setIntegrationsLoading(true);
      setIntegrationsError(null);
      try {
        const response = await listOrgIntegrations(orgId, {
          includeInactive: true,
        });
        setIntegrations(response.integrations || []);
      } catch (err: any) {
        setIntegrationsError(
          err?.message || "Failed to load organization integrations.",
        );
      } finally {
        setIntegrationsLoading(false);
      }
    },
    [listOrgIntegrations, orgId],
  );

  useEffect(() => {
    refreshIntegrations();
  }, [refreshIntegrations]);

  useEffect(() => {
    if (!orgId || !isAdmin) return;

    const feedback = oauthCallbackFeedbackFromSearch(location.search);
    if (!feedback) return;

    setOAuthFeedback(feedback);
    if (feedback.color === "success") {
      void Promise.all([refreshIntegrations(), refreshCatalog()]);
    }

    const nextSearch = removeOAuthCallbackParams(location.search);
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch,
      },
      { replace: true },
    );
  }, [
    location.pathname,
    location.search,
    navigate,
    orgId,
    isAdmin,
    refreshCatalog,
    refreshIntegrations,
  ]);

  if (userLoading) return <Loading />;
  if (userError) return <PageError>{userError}</PageError>;
  if (!isAdmin) return <Navigate to="/" replace />;
  if (!orgId) return <PageError>Organization context is required.</PageError>;

  const handleSaveConfig = async (
    entry: IntegrationCatalogEntry,
    values: Record<string, any>,
    targetIntegration?: OrganizationIntegration,
  ) => {
    const existingIntegration =
      targetIntegration || integrationForEntry(entry, integrations);
    const { config, secretKey } = configAndSecretForEntry(entry, values);

    if (existingIntegration) {
      const payload: OrganizationIntegrationUpdate = {
        status: "active",
        config,
      };
      if (secretKey) {
        payload.secret_key = secretKey;
      }
      await updateOrgIntegration(orgId, existingIntegration.id, payload);
    } else {
      const payload: OrganizationIntegrationCreate = {
        name: entry.display_name,
        type: entry.key,
        config,
        secret_key: secretKey,
      };
      await createOrgIntegration(orgId, payload);
    }
    await Promise.all([refreshIntegrations(), refreshCatalog()]);
  };

  const handleDisable = async (integration: OrganizationIntegration) => {
    await updateOrgIntegration(orgId, integration.id, { status: "inactive" });
    await Promise.all([refreshIntegrations(), refreshCatalog()]);
  };

  const handleDeleteConfig = async (integration: OrganizationIntegration) => {
    await deleteOrgIntegration(orgId, integration.id);
    await Promise.all([refreshIntegrations(), refreshCatalog()]);
  };

  return (
    <AdminIntegrationsView
      catalogEntries={entries}
      integrations={integrations}
      orgId={orgId}
      loading={catalogLoading || integrationsLoading}
      error={catalogError || integrationsError}
      usingStub={usingStub}
      showDeprecated={showDeprecated}
      onToggleShowDeprecated={setShowDeprecated}
      onSaveConfig={handleSaveConfig}
      onDisable={handleDisable}
      onDeleteConfig={handleDeleteConfig}
      onReadDisableImpact={readDisableImpact}
      onTestDeltekConnection={(integration) =>
        testDeltekConnection(orgId, integration.id)
      }
      oauthFeedback={oauthFeedback}
    />
  );
};

export default AdminIntegrations;
